import { IconButton, Tooltip } from '@mui/material';

import { ContentCopyOutlined } from '@mui/icons-material';

import { useSnackbar } from '@/hooks/useSnackbar';

import { graphql, type DocumentType } from '@/gql';

import { useCopyMarkdown } from '@/modules/assistant/hooks/useMarkdownCopy';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import {
  ASSISTANT_SOURCES_RESPONSE_HEADING_TEXT,
  ASSISTANT_KNOWLEDGE_SOURCES_RESPONSE_HEADING_TEXT,
  ASSISTANT_INTERCOM_SOURCES_RESPONSE_HEADING_TEXT,
} from '../utils';

const ASSISTANT_AI_RESPONSE_COPY_FRAGMENT = graphql(`
  fragment assistantAiResponseFeedbackCopyItemFields on OrtooGptConversationItem {
    response {
      responseText
      knowledgeSourcesConnection {
        edges {
          node {
            title
            url
          }
        }
      }
      intercomSourcesConnection {
        edges {
          node {
            title
            url
          }
        }
      }
    }
  }
`);

interface Props {
  item: DocumentType<typeof ASSISTANT_AI_RESPONSE_COPY_FRAGMENT>;
}

export const AssistantAiResponseCopyButton = ({ item }: Props) => {
  const { response } = item ?? {};
  const {
    responseText,
    knowledgeSourcesConnection,
    intercomSourcesConnection,
  } = response ?? {};

  const { enqueueSnackbar } = useSnackbar();

  const knowledgeSources = extractConnectionProps(knowledgeSourcesConnection);
  const intercomSources = extractConnectionProps(intercomSourcesConnection);
  const sourcesMarkdownTitle = `## ${ASSISTANT_SOURCES_RESPONSE_HEADING_TEXT}`;
  const knowledgeSourcesMarkdownTitle = `### ${ASSISTANT_KNOWLEDGE_SOURCES_RESPONSE_HEADING_TEXT}`;
  const intercomSourcesMarkdownTitle = `### ${ASSISTANT_INTERCOM_SOURCES_RESPONSE_HEADING_TEXT}`;
  const knowledgeSourcesMarkdown = knowledgeSources
    .map((source) => `- [${source.title}](${source.url})`)
    .join('\n');
  const intercomSourcesMarkdown = intercomSources
    .map((source) => `- [${source.title}](${source.url})`)
    .join('\n');
  const sourcesMarkdown = [
    sourcesMarkdownTitle,
    knowledgeSourcesMarkdownTitle,
    knowledgeSourcesMarkdown,
    intercomSourcesMarkdownTitle,
    intercomSourcesMarkdown,
  ].join('\n\n');

  const markdownToCopy = `${responseText}\n\n${sourcesMarkdown}`;

  const copyResponseToClipboard = useCopyMarkdown(markdownToCopy || '');

  return (
    <Tooltip title="Copy this response">
      <IconButton
        onClick={async () => {
          if (!responseText) {
            return;
          }

          await copyResponseToClipboard();
          enqueueSnackbar('Copied response to the clipboard');
        }}
      >
        <ContentCopyOutlined />
      </IconButton>
    </Tooltip>
  );
};
